import { ISpecializationCategory } from 'data/interfaces/Common';
import { ICV } from 'data/interfaces/ICV';
import { ProfileType } from 'data/interfaces/IRegistration';
import { IUserProfile } from 'data/interfaces/IUser';
import { IVacancy } from 'data/interfaces/IVacancy';
import type { TranslationFunction } from 'global';
import { CompensationType, Preset } from 'types/enums';
import { TranslateCompensationType, TranslateCurrency } from './enumsTranslations';
import Formatter from './formatter';
import ImageHelper from './ImageHelper';
import { translate } from './translate';
// Импортируем библиотеки
import audioBufferToWav from 'audiobuffer-to-wav';
import { OggOpusDecoderWebWorker } from 'ogg-opus-decoder';

export const getUserImageSource = (userProfile: IUserProfile, preset?: Preset): string | null => {
  switch (userProfile?.profileType) {
    case ProfileType.Employee:
      if (userProfile.useOwnLogo && userProfile.logo) {
        return ImageHelper.urlFromFile(userProfile.logo, preset || Preset.xsResize);
      } else if (!userProfile.useOwnLogo && userProfile?.avatar) {
        return ImageHelper.urlFromFile(userProfile?.avatar, preset || Preset.xsResize);
      } else {
        return null;
      }
    case ProfileType.Hirer:
      if (userProfile.logo) {
        return ImageHelper.urlFromFile(userProfile.logo, Preset.xsResize);
      } else {
        return null;
      }
    default:
      return null;
  }
};

export const getCategoryName = (category: ISpecializationCategory, lang: string) => {
  if (!category) return null;
  const found = category.translations.find((t) => t.languageCode === lang);
  return found?.name || category.name;
};

export const shareData = async (data: ShareData) => {
  try {
    await navigator.share(data);
  } catch (err) {
    console.warn(err);
  }
};

export const clearObject = <T>(obj: T, type?: 'string' | 'array') => {
  const clearedQuery: { [key: string]: any } = { ...obj };
  if (type === 'string') {
    Object.entries(clearedQuery).forEach(([key, value]) => {
      if (!Boolean(value) || (typeof value === 'string' && value.trim().length === 0))
        delete clearedQuery[key];
    });
  } else {
    Object.entries(clearedQuery).forEach(([key, value]) => {
      if (
        value === null ||
        value === undefined ||
        value === 0 ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'string' && value.trim().length === 0)
        // ||Object.keys(value).length === 0
        // ||Object.values(value).filter(Boolean).length === 0
      )
        delete clearedQuery[key];
    });
  }

  return clearedQuery as T;
};

export const getAge = (age: number): string => {
  if (!age) {
    return null;
  }

  return age + ' ' + translate('year', { count: age });
};

export const getSalary = (
  data: { cv?: ICV; vacancy?: IVacancy },
  t?: TranslationFunction,
): string | null => {
  const salary =
    (data.cv &&
      Formatter.formatNumber(Number(data.cv?.salary)) +
        ' ' +
        TranslateCurrency(data.cv?.currency)) ||
    (data.vacancy &&
      [
        data.vacancy?.salaryMin &&
          t('from') +
            ' ' +
            Formatter.formatNumber(data.vacancy?.salaryMin) +
            TranslateCurrency(data.vacancy?.currency),
        data.vacancy?.salaryMax &&
          t('to') +
            ' ' +
            Formatter.formatNumber(data.vacancy?.salaryMax) +
            TranslateCurrency(data.vacancy?.currency),
      ]
        .filter(Boolean)
        .join(' '));
  const compensation = data.vacancy?.compensation || data.cv?.compensation;

  if (!Boolean(salary?.trim()) && compensation === CompensationType.Salary) {
    return null;
  }

  switch (compensation) {
    case CompensationType.Contract:
      return TranslateCompensationType(compensation, t);
    case CompensationType.Salary:
      return salary;
    default:
      return salary;
  }
};

export const getUserName = (profile: IUserProfile): string => {
  if (!profile) {
    return translate('profile_removed');
  } else {
    return [profile?.firstName, profile?.patronymic, profile?.lastName].filter(Boolean).join(' ');
  }
};

export function copyToClipboard(text: string): boolean {
  //  если поддерживается navigator.clipboard
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text);
    return true;
  } else {
    const isIos = navigator.userAgent.match(/ipad|iphone/i);
    const textarea = document.createElement('textarea');
    // create textarea
    textarea.value = text;
    // ios will zoom in on the input if the font-size is < 16px
    textarea.style.fontSize = '20px';
    document.body.appendChild(textarea);

    // select text
    if (isIos) {
      const range = document.createRange();
      range.selectNodeContents(textarea);

      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      textarea.setSelectionRange(0, 999999);
    } else {
      textarea.select();
    }

    // copy selection
    document.execCommand('copy');

    // cleanup
    document.body.removeChild(textarea);
  }
}

export function daysBetween(startDate: Date, endDate: Date) {
  if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
    throw new Error(translate('use_correct_date_objects'));
  }

  const diffTime = Math.abs(
    Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()) -
      Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()),
  );
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
}

export function decodeOgg(arrayBuffer: ArrayBuffer, callback?: (blob: Blob) => void) {
  if (typeof window === 'undefined') return;

  (async () => {
    const decoder = new OggOpusDecoderWebWorker();
    try {
      const decoded = await decoder.decode(new Uint8Array(arrayBuffer));

      // Создаем AudioBuffer на основе декодированных данных
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const audioBuffer = audioCtx.createBuffer(
        decoded.channelData.length,
        decoded.channelData[0].length,
        16000, // Устанавливаем желаемую частоту 16kHz
      );

      // Загружаем данные каналов в AudioBuffer
      for (let channel = 0; channel < decoded.channelData.length; channel++) {
        audioBuffer.getChannelData(channel).set(decoded.channelData[channel]);
      }

      // Конвертируем AudioBuffer в WAV с использованием библиотеки
      const wavBuffer = audioBufferToWav(audioBuffer);

      // Создаем Blob из WAV буфера
      callback?.(new Blob([wavBuffer], { type: 'audio/wav' }));
    } catch (error) {
      console.error('Error decoding Opus audio:', error);
    } finally {
      await decoder.free();
    }
  })();
}

export const getVacancyTimeColor = (vacancy: IVacancy): string => {
  const publishedAt = new Date(vacancy.publishedAt);
  publishedAt.setDate(publishedAt.getDate() + 30);

  const _daysBetween = daysBetween(publishedAt, new Date());

  if (_daysBetween > 0 && _daysBetween <= 7) {
    return 'timeRemains_red';
  }
  if (_daysBetween > 7 && _daysBetween <= 14) {
    return 'timeRemains_orange';
  }
  if (_daysBetween > 14 && _daysBetween <= 30) {
    return 'timeRemains_green';
  }
};

export const calculateBuyer = (
  categoryName: string,
  geos: ICV['geos'],
  sources: ICV['sources'],
  verticals: ICV['verticals'],
): boolean => {
  if (!!geos?.length && !!sources?.length && !!verticals?.length) {
    return false;
  }

  const BUYER_CATEGORY_ENDS_WITH = ['buyer', 'buying'];

  return (
    BUYER_CATEGORY_ENDS_WITH.filter((end) => categoryName?.toLowerCase().endsWith(end)).length !== 0
  );
};
